import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bot, Check, Copy, Plus, Settings2, Trash2, ChevronDown, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { authHeaders } from "@/lib/authToken";
import { formatDistanceToNow } from "date-fns";
import { McpDataGrantEditor } from "@/components/mcp/McpDataGrantEditor";
import { LibrarianCoverageCard } from "@/components/mcp/LibrarianCoverageCard";
import { McpTokenSavingsCard } from "@/components/mcp/McpTokenSavingsCard";
import { LibrarianPreviewCard } from "@/components/mcp/LibrarianPreviewCard";
import { LibrarianLensesCard } from "@/components/mcp/LibrarianLensesCard";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  cloneMcpGrants,
  parseMcpGrants,
  summarizeMcpGrants,
  type McpGrants,
} from "@shared/mcpGrants";

type ApiKeyRecord = {
  id: string;
  name: string;
  keyPrefix: string;
  scopes: string[];
  status: "active" | "revoked";
  lastUsedAt: string | null;
  createdAt: string | null;
  mcpGrants?: McpGrants;
};

async function v1Fetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await authHeaders();
  const res = await fetch(`/api/v1${path}`, { ...init, headers: { ...headers, ...init?.headers } });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
  }
  return body as T;
}

const MCP_ENDPOINT = "https://www.deeplysmart.app/api/mcp";
const CLAUDE_OAUTH_CMD = `claude mcp add --transport http deeplysmart ${MCP_ENDPOINT}`;
const CODEX_TOML = `[mcp_servers.deeplysmart]
url = "${MCP_ENDPOINT}"`;
const CURSOR_JSON = `{
  "mcpServers": {
    "deeplysmart": {
      "url": "${MCP_ENDPOINT}"
    }
  }
}`;

type ConnectorCardDef = {
  id: string;
  name: string;
  blurb: string;
  openLabel?: string;
  openUrl?: string;
  steps: string[];
  note?: string;
};

const CONNECTORS: ConnectorCardDef[] = [
  {
    id: "claude",
    name: "Claude",
    blurb: "Visual Connectors page — no config file",
    openLabel: "Open Claude",
    openUrl: "https://claude.ai/settings/connectors",
    steps: [
      "Customize → Connectors → Add custom connector",
      "Paste the Librarian URL (leave Advanced client id and secret empty)",
      "Click Add, then Connect — sign in here and Allow",
    ],
  },
  {
    id: "chatgpt-desktop",
    name: "ChatGPT desktop (Codex)",
    blurb: "Settings → Plugins → Add — not General, not search labels",
    steps: [
      "Settings → search mcp → open Plugins (Manage plugins, skills, and MCPs)",
      "Click Add (top right). Ignore unclickable “MCP servers” search rows",
      "Streamable HTTP, paste the Librarian URL, no Bearer key",
      "Authenticate, or run: codex mcp login deeplysmart — then Allow here",
    ],
    note: "Search hits like “MCP servers” are often not links. The Add menu on the Plugins page is the real form.",
  },
  {
    id: "chatgpt-web",
    name: "ChatGPT on the web",
    blurb: "chatgpt.com Apps / Developer mode — not the desktop app",
    openLabel: "Open ChatGPT",
    openUrl: "https://chatgpt.com",
    steps: [
      "Profile → Settings → Apps → Advanced settings → Developer mode",
      "Create a connector, paste the Librarian URL, Authentication = OAuth",
      "Create — sign in here and Allow",
    ],
    note: "Missing on many personal accounts. If you do not see Apps / Developer mode, use ChatGPT desktop instead.",
  },
  {
    id: "grok",
    name: "Grok",
    blurb: "Visual Connectors page — no config file",
    openLabel: "Open Grok",
    openUrl: "https://grok.com/connectors",
    steps: [
      "New Connector → Custom",
      "Paste the Librarian URL as the Server URL",
      "If asked for OAuth fields, leave the secret blank — then sign in here and Allow",
    ],
  },
];

function connectCommand(key: string): string {
  return `claude mcp add --transport http deeplysmart ${MCP_ENDPOINT} --header "Authorization: Bearer ${key}"`;
}

function grantsOf(conn: Pick<ApiKeyRecord, "mcpGrants">): McpGrants {
  return cloneMcpGrants(parseMcpGrants(conn.mcpGrants).grants);
}

export default function McpConnections() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [createGrants, setCreateGrants] = useState<McpGrants>(() => cloneMcpGrants());
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedCmd, setCopiedCmd] = useState(false);
  const [copiedSnippet, setCopiedSnippet] = useState<string | null>(null);

  const [editTarget, setEditTarget] = useState<ApiKeyRecord | null>(null);
  const [editGrants, setEditGrants] = useState<McpGrants>(() => cloneMcpGrants());

  const keysQuery = useQuery({
    queryKey: ["v1-api-keys"],
    queryFn: () => v1Fetch<{ data: ApiKeyRecord[] }>("/keys").then((r) => r.data),
  });

  const connections = (keysQuery.data ?? []).filter((k) => k.scopes.includes("mcp"));

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["v1-api-keys"] });

  const createMutation = useMutation({
    mutationFn: ({ name, mcpGrants }: { name: string; mcpGrants: McpGrants }) =>
      v1Fetch<{ data: ApiKeyRecord & { key: string } }>("/keys", {
        method: "POST",
        body: JSON.stringify({ name, scopes: ["mcp"], mcpGrants }),
      }),
    onSuccess: (result) => {
      setCreateOpen(false);
      setNewName("");
      setCreateGrants(cloneMcpGrants());
      setCreatedKey(result.data.key);
      setCopiedKey(false);
      setCopiedCmd(false);
      invalidate();
    },
    onError: (e: Error) =>
      toast({ title: "Failed to create connection", description: e.message, variant: "destructive" }),
  });

  const saveGrantsMutation = useMutation({
    mutationFn: ({ id, mcpGrants }: { id: string; mcpGrants: McpGrants }) =>
      v1Fetch(`/keys/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ mcpGrants }),
      }),
    onSuccess: () => {
      toast({ title: "Data access updated" });
      setEditTarget(null);
      invalidate();
    },
    onError: (e: Error) => toast({ title: "Update failed", description: e.message, variant: "destructive" }),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      v1Fetch(`/keys/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: active ? "active" : "revoked" }),
      }),
    onSuccess: () => invalidate(),
    onError: (e: Error) => toast({ title: "Update failed", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => v1Fetch(`/keys/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast({ title: "Connection removed" });
      invalidate();
    },
    onError: (e: Error) => toast({ title: "Delete failed", description: e.message, variant: "destructive" }),
  });

  const copyText = async (text: string, mark: (v: boolean) => void) => {
    await navigator.clipboard.writeText(text);
    mark(true);
    toast({ title: "Copied to clipboard" });
  };

  const openEdit = (conn: ApiKeyRecord) => {
    setEditGrants(cloneMcpGrants(grantsOf(conn)));
    setEditTarget(conn);
  };

  return (
    <div className="container mx-auto max-w-4xl p-4 md:p-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Bot className="h-6 w-6" /> Connect AI tools
        </h1>
        <p className="text-muted-foreground mt-1">
          Open Claude, ChatGPT, or Grok connectors, paste the Librarian URL, then sign in here and click
          Allow. Each connection only sees{" "}
          <span className="font-medium text-foreground">your own</span> granted data — revoke it anytime below.
        </p>
      </div>

      <div className="grid gap-4">
        {CONNECTORS.map((c) => (
          <ConnectorCard
            key={c.id}
            connector={c}
            endpoint={MCP_ENDPOINT}
            copied={copiedSnippet === `${c.id}-url`}
            onCopyUrl={() => copyText(MCP_ENDPOINT, (v) => setCopiedSnippet(v ? `${c.id}-url` : null))}
          />
        ))}
      </div>

      <Collapsible>
        <div className="flex items-center justify-between gap-4">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="text-muted-foreground px-0">
              <ChevronDown className="h-4 w-4 mr-1" /> Advanced: CLI, config files, or paste API key
            </Button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent className="space-y-4 pt-2">
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              For clients with no Connectors screen. Each pasted key can be revoked independently.
            </p>
            <Button
              size="sm"
              onClick={() => {
                setCreateGrants(cloneMcpGrants());
                setCreateOpen(true);
              }}
            >
              <Plus className="h-4 w-4 mr-1" /> New connection
            </Button>
          </div>
          <SnippetBlock
            label="Claude Code"
            value={CLAUDE_OAUTH_CMD}
            copied={copiedSnippet === "claude-cli"}
            onCopy={() => copyText(CLAUDE_OAUTH_CMD, (v) => setCopiedSnippet(v ? "claude-cli" : null))}
          />
          <SnippetBlock
            label="Codex (~/.codex/config.toml)"
            value={CODEX_TOML}
            copied={copiedSnippet === "codex"}
            onCopy={() => copyText(CODEX_TOML, (v) => setCopiedSnippet(v ? "codex" : null))}
          />
          <SnippetBlock
            label="Cursor (.cursor/mcp.json)"
            value={CURSOR_JSON}
            copied={copiedSnippet === "cursor"}
            onCopy={() => copyText(CURSOR_JSON, (v) => setCopiedSnippet(v ? "cursor" : null))}
          />
        </CollapsibleContent>
      </Collapsible>

      <LibrarianCoverageCard />
      <McpTokenSavingsCard />
      <LibrarianPreviewCard grants={createGrants} />
      <LibrarianLensesCard />

      <Card>
        <CardContent className="p-0">
          {keysQuery.isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Loading connections…</p>
          ) : keysQuery.isError ? (
            <p className="p-6 text-sm text-destructive">
              {(keysQuery.error as Error)?.message ?? "Failed to load connections"}
            </p>
          ) : connections.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">
              Connections appear here after you click Allow in Claude, ChatGPT, or Grok. Create a key only
              for clients that have no Connectors screen.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Key</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Last used</TableHead>
                  <TableHead>Enabled</TableHead>
                  <TableHead className="w-20" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {connections.map((conn) => (
                  <TableRow key={conn.id}>
                    <TableCell className="font-medium">{conn.name}</TableCell>
                    <TableCell>
                      <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{conn.keyPrefix}…</code>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[180px] truncate">
                      {summarizeMcpGrants(grantsOf(conn))}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {conn.lastUsedAt
                        ? formatDistanceToNow(new Date(conn.lastUsedAt), { addSuffix: true })
                        : "never"}
                      {(!conn.lastUsedAt || Date.now() - new Date(conn.lastUsedAt).getTime() > 7 * 24 * 60 * 60 * 1000) && (
                        <Badge variant="outline" className="ml-2 text-[10px]">unused 7d</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={conn.status === "active"}
                          onCheckedChange={(checked) =>
                            toggleMutation.mutate({ id: conn.id, active: checked })
                          }
                        />
                        <Badge variant={conn.status === "active" ? "default" : "secondary"} className="text-[10px]">
                          {conn.status === "active" ? "on" : "off"}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          title="Configure data"
                          onClick={() => openEdit(conn)}
                        >
                          <Settings2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => deleteMutation.mutate(conn.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New connection</DialogTitle>
            <DialogDescription>
              Generates a key scoped only to MCP tool access — shown once, right after creation. Choose which
              data this AI may read.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="conn-name">Name</Label>
              <Input
                id="conn-name"
                placeholder="e.g. Claude Code"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </div>
            <McpDataGrantEditor value={createGrants} onChange={setCreateGrants} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!newName.trim() || createMutation.isPending}
              onClick={() => createMutation.mutate({ name: newName.trim(), mcpGrants: createGrants })}
            >
              {createMutation.isPending ? "Creating…" : "Create connection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={editTarget !== null}
        onOpenChange={(open) => {
          if (!open) setEditTarget(null);
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Configure data — {editTarget?.name}</DialogTitle>
            <DialogDescription>
              Changes apply immediately to this connection. The API key does not need to be rotated.
            </DialogDescription>
          </DialogHeader>
          <McpDataGrantEditor value={editGrants} onChange={setEditGrants} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>
              Cancel
            </Button>
            <Button
              disabled={!editTarget || saveGrantsMutation.isPending}
              onClick={() => editTarget && saveGrantsMutation.mutate({ id: editTarget.id, mcpGrants: editGrants })}
            >
              {saveGrantsMutation.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createdKey !== null} onOpenChange={(open) => !open && setCreatedKey(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Connection created</DialogTitle>
            <DialogDescription>Copy this key now — for security it cannot be shown again.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Key</Label>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-muted rounded-md p-3 text-xs break-all">{createdKey}</code>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => createdKey && copyText(createdKey, setCopiedKey)}
                >
                  {copiedKey ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Connect from Claude Code</Label>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-muted rounded-md p-3 text-xs break-all">
                  {createdKey ? connectCommand(createdKey) : ""}
                </code>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => createdKey && copyText(connectCommand(createdKey), setCopiedCmd)}
                >
                  {copiedCmd ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div className="space-y-1 text-xs text-muted-foreground">
              <p>
                Prefer the Connectors screens in Claude, ChatGPT, or Grok with{" "}
                <code>{MCP_ENDPOINT}</code> and no header. Paste this key only if the client cannot open
                OAuth.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setCreatedKey(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ConnectorCard({
  connector,
  endpoint,
  copied,
  onCopyUrl,
}: {
  connector: ConnectorCardDef;
  endpoint: string;
  copied: boolean;
  onCopyUrl: () => void;
}) {
  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium">{connector.name}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{connector.blurb}</p>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button variant="outline" size="sm" onClick={onCopyUrl}>
              {copied ? <Check className="h-4 w-4 mr-1 text-emerald-500" /> : <Copy className="h-4 w-4 mr-1" />}
              Copy URL
            </Button>
            {connector.openUrl && connector.openLabel ? (
              <Button size="sm" asChild>
                <a href={connector.openUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4 mr-1" />
                  {connector.openLabel}
                </a>
              </Button>
            ) : null}
          </div>
        </div>
        <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
          {connector.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
        {connector.note ? <p className="text-xs text-muted-foreground">{connector.note}</p> : null}
        <code className="block bg-muted rounded-md p-3 text-xs break-all">{endpoint}</code>
      </CardContent>
    </Card>
  );
}

function SnippetBlock({
  label,
  value,
  copied,
  onCopy,
}: {
  label: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="flex items-start gap-2">
        <code className="flex-1 bg-muted rounded-md p-3 text-xs whitespace-pre-wrap break-all">{value}</code>
        <Button variant="outline" size="icon" onClick={onCopy}>
          {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}
